/**
 * @file: useAlert.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import type { AlertScreenProps } from '@euler/app/flows/common/AlertScreen';
import { getGlobalNavigationRef } from '@euler/app/navigation/RoutesContainer';
import { kDefaultOkButtonText } from '@euler/functions/constants';
import { NavigationContainerRefContext } from '@euler/functions/navigation/NavigationContainerRefContext';
import { StackActions } from '@react-navigation/native';
import { useCallback, useContext } from 'react';
import { Alert, Platform } from 'react-native';

export const useAlert = () => {
  const navigationContext = useContext(NavigationContainerRefContext);
  return useCallback(
    async (props: AlertScreenProps) => {
      if (Platform.OS === 'web') {
        await new Promise<void>(resolve => {
          const navigationRef =
            navigationContext?.navigationRef ?? getGlobalNavigationRef();
          navigationRef.current?.dispatch(
            StackActions.push('_alert', {
              ...props,
              buttons: props.buttons?.map(button => ({
                ...button,
                onPress: button.onPress
                  ? (value?: string) => {
                      button.onPress?.(value);
                      resolve();
                    }
                  : undefined,
              })),
            }),
          );
        });
      } else {
        await new Promise<void>(resolve => {
          Alert.alert(
            props.title,
            props.message,
            props.buttons != null && props.buttons.length > 0
              ? props.buttons.map(button => ({
                  ...button,
                  onPress: () => {
                    button.onPress?.();
                    resolve();
                  },
                }))
              : [
                  {
                    text: props.okButtonText ?? kDefaultOkButtonText,
                    onPress: () => {
                      props.onOk?.();
                      resolve();
                    },
                  },
                ],
          );
        });
      }
    },
    [navigationContext],
  );
};
